<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['stock_levels']==1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show <b-form-select class="form-select form-select-sm mx-1" v-model="perPage" size="sm" :options="Options"></b-form-select> entries
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                  <label class="d-inline-flex align-items-center">
                    Search: <b-form-input v-model="filter" type="search" placeholder="Search..." class="form-control form-control-sm ms-2"></b-form-input>
                  </label>
                </div>
              </div>
            </div>
            <div class="table-responsive">
              <b-table
                :items="stockBatches"
                :fields="fields"
                responsive="sm"
                :busy="isBusy"
              >
                <template #table-busy><div class="text-center text-primary my-2"><b-spinner class="align-middle" type="grow" small></b-spinner> <strong>No Data Found</strong></div></template>
                <template #cell(index)="data">
                  {{ ((currentPage - 1) * perPage) + data.index + 1 }}
                </template>
                <template #cell(product)="data">
                  {{ data.item.product.name }}
                </template>
                <template #cell(expiry_date)="data">
                  {{ formatExpiryDate(data.item.expiry_date) }}
                </template>
                <template #cell(received_unit)="data">
                  {{ data.item.received_unit ? data.item.received_unit.unit_name : "N/A" }}
                </template>
                <template #cell(status_badge)="data">
                  <span :class="['status-pill', batchStatus(data.item).className]">
                    <span class="status-dot"></span>
                    <span class="status-label">{{ batchStatus(data.item).label }}</span>
                  </span>
                </template>
                <template #cell(actions)="data">
                  <b-button size="sm" variant="primary" @click="openEditBatch(data.item)">
                    <i class="mdi mdi-square-edit-outline"></i>
                  </b-button>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                  <div class="dataTables_paginate paging_simple_numbers float-end">
                    <ul class="pagination pagination-rounded mb-0">
                      <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
                    </ul>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div>
            <img src="@/assets/images/profile-img.png" alt class="img-fluid" /><br><br>
            <center><span class="text-primary h6">You have no permission to View Stock Levels</span></center>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="edit-batch" centered title="Update Batch Stock" title-class="font-18" hide-footer>
      <div class="batch-modal-banner">
        <div class="batch-modal-banner-icon">
          <i class="mdi mdi-clipboard-edit-outline"></i>
        </div>
        <div class="batch-modal-banner-copy">
          <div class="batch-modal-banner-title">Adjust this batch</div>
          <div class="batch-modal-banner-sub">
            Hover the <i class="mdi mdi-information-outline"></i> next to any field to see what it means.
          </div>
        </div>
      </div>

      <form @submit.prevent="updateBatch">
        <div class="batch-section">
          <div class="batch-section-title">Batch identity</div>

          <div class="form-group mb-2">
            <label class="field-label" for="batch-input-product">
              <span>Product</span>
              <i class="mdi mdi-information-outline help-icon" id="batch-help-product" tabindex="0"></i>
            </label>
            <b-popover id="popover-batch-product" target="batch-help-product" triggers="hover focus" placement="right" custom-class="batch-help-popover">
              <template #title>
                <div class="batch-help-popover-titlebar">
                  <span>About: Product</span>
                  <button type="button" class="batch-help-popover-close" aria-label="Close" @click="closeHelp('popover-batch-product')">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </template>
              This is the product the batch belongs to. It is shown here for reference only and cannot be changed from this dialog &mdash; if the wrong product was linked, ask an administrator to correct it.
            </b-popover>
            <input id="batch-input-product" :value="selectedBatchProduct" type="text" readonly class="form-control" />
          </div>

          <div class="form-group mb-2">
            <label class="field-label" for="batch-input-number">
              <span>Batch Number</span>
              <i class="mdi mdi-information-outline help-icon" id="batch-help-number" tabindex="0"></i>
            </label>
            <b-popover id="popover-batch-number" target="batch-help-number" triggers="hover focus" placement="right" custom-class="batch-help-popover">
              <template #title>
                <div class="batch-help-popover-titlebar">
                  <span>About: Batch Number</span>
                  <button type="button" class="batch-help-popover-close" aria-label="Close" @click="closeHelp('popover-batch-number')">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </template>
              The unique identifier printed on the batch by the manufacturer. Update this only if the original batch number was missing, mistyped, or entered wrongly when the batch was first received.
            </b-popover>
            <input id="batch-input-number" v-model="editForm.batch_number" type="text" required class="form-control" />
          </div>

          <div class="form-group mb-2">
            <label class="field-label" for="batch-input-expiry">
              <span>Expiry Date</span>
              <i class="mdi mdi-information-outline help-icon" id="batch-help-expiry" tabindex="0"></i>
            </label>
            <b-popover id="popover-batch-expiry" target="batch-help-expiry" triggers="hover focus" placement="right" custom-class="batch-help-popover">
              <template #title>
                <div class="batch-help-popover-titlebar">
                  <span>About: Expiry Date</span>
                  <button type="button" class="batch-help-popover-close" aria-label="Close" @click="closeHelp('popover-batch-expiry')">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </template>
              The real expiry date printed on the batch. The system uses this to apply FEFO (First-Expired-First-Out) when dispensing and to raise warnings about 10 days before expiry. Setting it correctly is important for safe stock rotation.
            </b-popover>
            <input id="batch-input-expiry" v-model="editForm.expiry_date" type="date" class="form-control" />
          </div>
        </div>

        <div class="batch-section">
          <div class="batch-section-title">Quantities</div>

          <div class="form-group mb-2">
            <label class="field-label" for="batch-input-received">
              <span>Received Quantity (Base)</span>
              <i class="mdi mdi-information-outline help-icon" id="batch-help-received" tabindex="0"></i>
            </label>
            <b-popover id="popover-batch-received" target="batch-help-received" triggers="hover focus" placement="right" custom-class="batch-help-popover">
              <template #title>
                <div class="batch-help-popover-titlebar">
                  <span>About: Received Quantity (Base)</span>
                  <button type="button" class="batch-help-popover-close" aria-label="Close" @click="closeHelp('popover-batch-received')">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </template>
              The total quantity that originally came in with this batch, expressed in <strong>base units</strong> &mdash; the smallest unit of the product (e.g. tablets, capsules, pieces). This represents the full received amount, before any sales or dispensing.
            </b-popover>
            <input id="batch-input-received" v-model="editForm.quantity_received_base" type="number" min="0" required class="form-control" />
          </div>

          <div class="form-group mb-3">
            <label class="field-label" for="batch-input-remaining">
              <span>Remaining Quantity (Base)</span>
              <i class="mdi mdi-information-outline help-icon" id="batch-help-remaining" tabindex="0"></i>
            </label>
            <b-popover id="popover-batch-remaining" target="batch-help-remaining" triggers="hover focus" placement="right" custom-class="batch-help-popover">
              <template #title>
                <div class="batch-help-popover-titlebar">
                  <span>About: Remaining Quantity (Base)</span>
                  <button type="button" class="batch-help-popover-close" aria-label="Close" @click="closeHelp('popover-batch-remaining')">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </template>
              The current balance left in this batch in <strong>base units</strong>. This must never be greater than the Received Quantity &mdash; if it is, the save will be blocked. Use this field to correct stock counts after physical recounts or audit adjustments.
            </b-popover>
            <input id="batch-input-remaining" v-model="editForm.quantity_remaining_base" type="number" min="0" required class="form-control" />
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <b-button type="button" variant="secondary" @click="$bvModal.hide('edit-batch')" :disabled="isSavingBatch">Close</b-button>
          <b-button type="submit" variant="primary" class="ms-2" :disabled="isSavingBatch">
            <b-spinner v-if="isSavingBatch" small class="align-middle me-1"></b-spinner>
            <span>{{ isSavingBatch ? 'Saving...' : 'Save Changes' }}</span>
          </b-button>
        </div>
      </form>
    </b-modal>
  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Batch Stock Levels",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout, PageHeader
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Batch Stock Levels",
      items: [{ text: "Dashboard", to: "/" }, { text: "Batch Stock Levels", active: true }],
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: "",
      isBusy: true,
      totalRows: 0,
      searchDebounce: null,
      stockBatches: [],
      isSavingBatch: false,
      selectedBatchProduct: "",
      editForm: {
        id: "",
        batch_number: "",
        expiry_date: "",
        quantity_received_base: 0,
        quantity_remaining_base: 0,
      },
      fields: [
        { key: "index", label: "#" },
        { key: "product", label: "Product" },
        { key: "batch_number", label: "Batch Number" },
        { key: "expiry_date", label: "Expiry Date" },
        { key: "received_unit", label: "Received Unit" },
        { key: "quantity_received_base", label: "Received (Base)" },
        { key: "quantity_remaining_base", label: "Remaining (Base)" },
        { key: "status_badge", label: "Status" },
        { key: "actions", label: "Actions" },
      ],
    };
  },
  mounted() {
    this.fetchStockBatches();
    document.addEventListener("click", this.handleOutsideHelpClick);
  },
  beforeDestroy() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
    document.removeEventListener("click", this.handleOutsideHelpClick);
  },
  watch: {
    currentPage() {
      this.fetchStockBatches();
    },
    perPage() {
      this.currentPage = 1;
      this.fetchStockBatches();
    },
    filter() {
      this.currentPage = 1;
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchStockBatches();
      }, 300);
    },
  },
  methods: {
    closeHelp(popoverId) {
      this.$root.$emit("bv::hide::popover", popoverId);
    },
    closeAllHelp() {
      this.$root.$emit("bv::hide::popover");
    },
    handleOutsideHelpClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(".help-icon") || target.closest(".batch-help-popover")) {
        return;
      }
      this.closeAllHelp();
    },
    async fetchStockBatches() {
      this.isBusy = true;
      try {
        const response = await axios.get("/stockbatches", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            search: this.filter || "",
          },
        });
        this.stockBatches = response.data.batches || [];
        this.totalRows = response.data.pagination?.total || 0;
      } finally {
        this.isBusy = false;
      }
    },
    openEditBatch(item) {
      this.selectedBatchProduct = item.product?.name || "";
      this.editForm = {
        id: item.id,
        batch_number: item.batch_number || "",
        expiry_date: item.expiry_date || "",
        quantity_received_base: item.quantity_received_base,
        quantity_remaining_base: item.quantity_remaining_base,
      };
      this.$bvModal.show("edit-batch");
    },
    async updateBatch() {
      if (Number(this.editForm.quantity_remaining_base) > Number(this.editForm.quantity_received_base)) {
        this.$toast.error("Remaining quantity cannot be greater than received quantity.");
        return;
      }

      const formData = new FormData();
      formData.append("id", this.editForm.id);
      formData.append("batch_number", this.editForm.batch_number);
      formData.append("expiry_date", this.editForm.expiry_date || "");
      formData.append("quantity_received_base", this.editForm.quantity_received_base);
      formData.append("quantity_remaining_base", this.editForm.quantity_remaining_base);

      this.isSavingBatch = true;
      try {
        await axios.post("/updateproductbatch", formData);
        await this.fetchStockBatches();
        this.$bvModal.hide("edit-batch");
        this.$toast.success("Batch stock updated.");
      } finally {
        this.isSavingBatch = false;
      }
    },
    daysUntilExpiry(expiryDate) {
      if (!expiryDate) return null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);
      return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    },
    batchStatus(item) {
      const days = this.daysUntilExpiry(item.expiry_date);
      if (item.is_expired || (days !== null && days < 0)) {
        return { label: "Expired", className: "expired" };
      }
      if (days !== null && days <= 10) {
        return { label: `Expiring Soon (${days} day${days === 1 ? "" : "s"})`, className: "expiring" };
      }
      if (item.is_legacy_opening) {
        return { label: "Legacy", className: "legacy" };
      }
      if (item.is_open) {
        return { label: "Open", className: "open" };
      }
      return { label: "Live", className: "live" };
    },
    formatExpiryDate(expiryDate) {
      if (!expiryDate) {
        return "No expiry recorded";
      }
      const date = new Date(expiryDate);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.batch-modal-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.9rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.batch-modal-banner-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
  color: #ffffff;
  font-size: 1.1rem;
  box-shadow: 0 8px 18px rgba(20, 184, 166, 0.22);
}

.batch-modal-banner-title {
  font-size: 0.92rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
}

.batch-modal-banner-sub {
  margin-top: 0.15rem;
  font-size: 0.76rem;
  color: #475569;
  line-height: 1.35;
}

.batch-modal-banner-sub i {
  color: #0f766e;
  font-size: 0.95rem;
  vertical-align: -2px;
}

.batch-section {
  margin-bottom: 0.85rem;
  padding: 0.85rem 0.95rem 0.4rem;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  background: #ffffff;
}

.batch-section-title {
  margin-bottom: 0.65rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2937;
}

.help-icon {
  font-size: 1rem;
  color: #94a3b8;
  cursor: help;
  transition: color 0.18s ease, transform 0.18s ease;
}

.help-icon:hover,
.help-icon:focus {
  color: #0f766e;
  transform: translateY(-1px);
  outline: none;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.18rem 0.6rem 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid transparent;
  background: transparent;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  flex: 0 0 7px;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.4);
}

.status-pill.live {
  background: rgba(34, 197, 94, 0.1);
  color: #166534;
}
.status-pill.live .status-dot {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
}

.status-pill.open {
  background: rgba(59, 130, 246, 0.1);
  color: #1d4ed8;
}
.status-pill.open .status-dot {
  background: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}

.status-pill.legacy {
  background: rgba(167, 139, 250, 0.12);
  color: #6d28d9;
}
.status-pill.legacy .status-dot {
  background: #a78bfa;
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.2);
}

.status-pill.expiring {
  background: rgba(249, 115, 22, 0.12);
  color: #c2410c;
}
.status-pill.expiring .status-dot {
  background: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.2);
}

.status-pill.expired {
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}
.status-pill.expired .status-dot {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}
</style>

<style>
.batch-help-popover.popover {
  max-width: 340px;
  min-width: 280px;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  background: #ffffff;
  font-family: inherit;
}

.batch-help-popover .popover-header {
  padding: 0.5rem 0.6rem 0.5rem 0.9rem;
  border-bottom: 1px solid #e2e8f0;
  border-top-left-radius: 13px;
  border-top-right-radius: 13px;
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%);
  color: #ffffff;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.batch-help-popover-titlebar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.batch-help-popover-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 0;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.18s ease;
}

.batch-help-popover-close:hover,
.batch-help-popover-close:focus {
  background: rgba(255, 255, 255, 0.35);
  transform: scale(1.06);
  outline: none;
}

.batch-help-popover-close i {
  font-size: 0.95rem;
}

.batch-help-popover .popover-body {
  padding: 0.85rem 0.95rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: #1f2937;
}

.batch-help-popover .popover-body strong {
  color: #0f766e;
  font-weight: 700;
}

.batch-help-popover.bs-popover-right > .arrow::after,
.batch-help-popover.bs-popover-auto[x-placement^="right"] > .arrow::after {
  border-right-color: #ffffff;
}

.batch-help-popover.bs-popover-right > .arrow::before,
.batch-help-popover.bs-popover-auto[x-placement^="right"] > .arrow::before {
  border-right-color: #cbd5e1;
}
</style>
